import React, { useState, useRef, useCallback, useMemo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Root, RootContent, Element, Text } from "hast";
import type { CampaignAsset, CharacterListItem } from "../../types/types";
import type { SessionTheme } from "../../config/sessionThemes";

// ─── Rehype plugin: transform [[asset:ID|Name]] and @[char:ID|Name] ───────────

function rehypeAssetRef() {
  function visitNode(node: Element | Root): void {
    const next: RootContent[] = [];
    for (const child of node.children) {
      if (
        child.type === "text" &&
        (child.value.includes("[[asset:") || child.value.includes("@[char:"))
      ) {
        next.push(...splitOnRefs(child.value));
      } else {
        if (child.type === "element") visitNode(child);
        next.push(child);
      }
    }
    node.children = next;
  }

  function splitOnRefs(text: string): (Element | Text)[] {
    const parts: (Element | Text)[] = [];
    // Combined regex: asset refs and character mentions
    const re = /\[\[asset:(\d+)\|([^\]]+)\]\]|@\[char:(\d+)\|([^\]]+)\]/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) {
        parts.push({ type: "text", value: text.slice(last, m.index) });
      }
      if (m[1] !== undefined) {
        // Asset ref: [[asset:ID|Name]]
        parts.push({
          type: "element",
          tagName: "span",
          properties: { "data-asset-id": m[1], "data-asset-display": m[2] },
          children: [{ type: "text", value: m[2] }],
        });
      } else {
        // Character mention: @[char:ID|Name]
        parts.push({
          type: "element",
          tagName: "span",
          properties: { "data-char-id": m[3], "data-char-display": m[4] },
          children: [{ type: "text", value: m[4] }],
        });
      }
      last = m.index + m[0].length;
    }
    if (last < text.length) {
      parts.push({ type: "text", value: text.slice(last) });
    }
    return parts;
  }

  return (tree: Root): void => visitNode(tree);
}

const REHYPE_PLUGINS = [rehypeAssetRef];

// ─── AssetRefChip ──────────────────────────────────────────────────────────────

interface AssetRefChipProps {
  assetId: number;
  displayName: string;
  assetsMap: Map<number, CampaignAsset>;
  theme: SessionTheme;
}

const AssetRefChip: React.FC<AssetRefChipProps> = ({ assetId, displayName, assetsMap, theme }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const chipRef = useRef<HTMLSpanElement>(null);

  const asset = assetsMap.get(assetId);
  const chipColor = asset ? theme.colors.accent : theme.colors.error;

  const show = useCallback(() => {
    const rect = chipRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({
        top: rect.bottom + 6,
        left: Math.min(rect.left, window.innerWidth - 300),
      });
    }
    setVisible(true);
  }, []);

  return (
    <>
      <span
        ref={chipRef}
        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium cursor-help mx-0.5"
        style={{
          backgroundColor: chipColor + "22",
          color: chipColor,
          border: `1px solid ${chipColor}55`,
        }}
        onMouseEnter={show}
        onMouseLeave={() => setVisible(false)}
        data-testid="asset-ref-chip"
      >
        {displayName}
      </span>

      {visible && (
        <div
          className="fixed z-[9999] w-72 rounded-lg shadow-2xl p-3 pointer-events-none"
          style={{
            top: pos.top,
            left: pos.left,
            backgroundColor: theme.colors.panelHeader,
            border: `1px solid ${theme.colors.panelBorder}`,
            color: theme.colors.textPrimary,
          }}
          data-testid="asset-ref-popover"
        >
          {asset ? (
            <>
              <p className="font-semibold text-sm mb-1">{asset.asset.name}</p>
              <div className="flex items-center gap-1.5 text-xs mb-2">
                <span style={{ color: theme.colors.accent }}>{asset.asset.type}</span>
                {asset.asset.category && (
                  <>
                    <span style={{ color: theme.colors.textMuted }}>·</span>
                    <span style={{ color: theme.colors.textMuted }}>{asset.asset.category}</span>
                  </>
                )}
              </div>
              {asset.asset.description && (
                <p className="text-xs mb-2" style={{ color: theme.colors.textSecondary }}>
                  {asset.asset.description.length > 200
                    ? asset.asset.description.slice(0, 200) + "…"
                    : asset.asset.description}
                </p>
              )}
              {asset.sections?.length > 0 && (
                <div
                  className="border-t pt-2 space-y-1.5"
                  style={{ borderColor: theme.colors.panelBorder }}
                >
                  {asset.sections.slice(0, 2).map((s) => (
                    <div key={s.id}>
                      <p
                        className="text-xs font-medium"
                        style={{ color: theme.colors.textPrimary }}
                      >
                        {s.name}
                      </p>
                      {s.content && (
                        <p className="text-xs" style={{ color: theme.colors.textMuted }}>
                          {s.content.length > 120 ? s.content.slice(0, 120) + "…" : s.content}
                        </p>
                      )}
                    </div>
                  ))}
                  {asset.sections.length > 2 && (
                    <p className="text-xs italic" style={{ color: theme.colors.textMuted }}>
                      +{asset.sections.length - 2} more section(s)
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-xs" style={{ color: theme.colors.error }}>
              Asset #{assetId} not found in this campaign.
            </p>
          )}
        </div>
      )}
    </>
  );
};

// ─── CharacterMentionChip ──────────────────────────────────────────────────────

interface CharacterMentionChipProps {
  charId: number;
  displayName: string;
  charactersMap: Map<number, CharacterListItem>;
  theme: SessionTheme;
}

const CharacterMentionChip: React.FC<CharacterMentionChipProps> = ({
  charId,
  displayName,
  charactersMap,
  theme,
}) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const chipRef = useRef<HTMLSpanElement>(null);

  const character = charactersMap.get(charId);
  // Use a distinct teal/cyan color for character mentions
  const chipColor = character ? "#06b6d4" : theme.colors.error;

  const show = useCallback(() => {
    const rect = chipRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({
        top: rect.bottom + 6,
        left: Math.min(rect.left, window.innerWidth - 260),
      });
    }
    setVisible(true);
  }, []);

  return (
    <>
      <span
        ref={chipRef}
        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium cursor-help mx-0.5"
        style={{
          backgroundColor: chipColor + "22",
          color: chipColor,
          border: `1px solid ${chipColor}55`,
        }}
        onMouseEnter={show}
        onMouseLeave={() => setVisible(false)}
        data-testid="char-mention-chip"
      >
        <span className="opacity-70">@</span>
        {displayName}
      </span>

      {visible && (
        <div
          className="fixed z-[9999] w-56 rounded-lg shadow-2xl p-3 pointer-events-none"
          style={{
            top: pos.top,
            left: pos.left,
            backgroundColor: theme.colors.panelHeader,
            border: `1px solid ${theme.colors.panelBorder}`,
            color: theme.colors.textPrimary,
          }}
          data-testid="char-mention-popover"
        >
          {character ? (
            <>
              <p className="font-semibold text-sm mb-1">{character.name}</p>
              {character.description && (
                <p className="text-xs" style={{ color: theme.colors.textSecondary }}>
                  {character.description.length > 120
                    ? character.description.slice(0, 120) + "…"
                    : character.description}
                </p>
              )}
            </>
          ) : (
            <p className="text-xs" style={{ color: theme.colors.error }}>
              Character #{charId} not found.
            </p>
          )}
        </div>
      )}
    </>
  );
};

// ─── MarkdownNotes ─────────────────────────────────────────────────────────────

export interface MarkdownNotesProps {
  value: string;
  onChange: (_v: string) => void;
  readOnly?: boolean;
  campaignAssets?: CampaignAsset[];
  sessionCharacters?: CharacterListItem[];
  theme: SessionTheme;
  placeholder?: string;
  /** data-testid applied to the textarea (edit mode) */
  testId?: string;
}

type AutocompleteMode = "asset" | "character" | null;

/** Detect if cursor is inside an open [[ trigger not yet closed with ]]. Returns query string or null. */
function detectAutocomplete(text: string, cursorPos: number): string | null {
  const before = text.slice(0, cursorPos);
  const triggerIdx = before.lastIndexOf("[[");
  if (triggerIdx === -1) return null;
  const afterTrigger = before.slice(triggerIdx + 2);
  if (afterTrigger.includes("]]")) return null;
  return afterTrigger;
}

/** Detect if cursor is after an open @ trigger not yet completed. Returns query string or null. */
function detectCharAutocomplete(text: string, cursorPos: number): string | null {
  const before = text.slice(0, cursorPos);
  const atIdx = before.lastIndexOf("@");
  if (atIdx === -1) return null;
  const afterAt = before.slice(atIdx + 1);
  // Already a completed ref (@[char:…])
  if (afterAt.startsWith("[char:")) return null;
  // Space or newline after @ means the trigger was abandoned
  if (/[\s\n]/.test(afterAt)) return null;
  return afterAt;
}

export const MarkdownNotes: React.FC<MarkdownNotesProps> = ({
  value,
  onChange,
  readOnly = false,
  campaignAssets = [],
  sessionCharacters = [],
  theme,
  placeholder = "Write your notes here…",
  testId = "notes-md",
}) => {
  const [mode, setMode] = useState<"edit" | "preview">(readOnly ? "preview" : "edit");
  const [autocompleteMode, setAutocompleteMode] = useState<AutocompleteMode>(null);
  const [autocompleteQuery, setAutocompleteQuery] = useState<string>("");
  const [activeIdx, setActiveIdx] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Map campaign-asset ID → CampaignAsset
  const assetsMap = useMemo(() => {
    const m = new Map<number, CampaignAsset>();
    for (const ca of campaignAssets) m.set(ca.id, ca);
    return m;
  }, [campaignAssets]);

  // Map character ID → CharacterListItem
  const charactersMap = useMemo(() => {
    const m = new Map<number, CharacterListItem>();
    for (const c of sessionCharacters) m.set(c.id, c);
    return m;
  }, [sessionCharacters]);

  // Filtered asset list for autocomplete
  const filteredAssets = useMemo(() => {
    if (autocompleteMode !== "asset") return [];
    const q = autocompleteQuery.toLowerCase();
    return campaignAssets.filter((ca) => !q || ca.asset.name.toLowerCase().includes(q)).slice(0, 8);
  }, [campaignAssets, autocompleteMode, autocompleteQuery]);

  // Filtered character list for autocomplete
  const filteredChars = useMemo(() => {
    if (autocompleteMode !== "character") return [];
    const q = autocompleteQuery.toLowerCase();
    return sessionCharacters.filter((c) => !q || c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [sessionCharacters, autocompleteMode, autocompleteQuery]);

  const dropdownItems = autocompleteMode === "asset" ? filteredAssets.length : filteredChars.length;

  // ── Autocomplete ─────────────────────────────────────────────────────────────

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const v = e.target.value;
      onChange(v);
      // Use length fallback when selectionStart is 0 (jsdom after fireEvent.change)
      const cursor = e.target.selectionStart || v.length;

      // Check asset trigger [[
      if (campaignAssets.length > 0) {
        const assetQuery = detectAutocomplete(v, cursor);
        if (assetQuery !== null) {
          setAutocompleteMode("asset");
          setAutocompleteQuery(assetQuery);
          setActiveIdx(0);
          return;
        }
      }

      // Check character trigger @
      if (sessionCharacters.length > 0) {
        const charQuery = detectCharAutocomplete(v, cursor);
        if (charQuery !== null) {
          setAutocompleteMode("character");
          setAutocompleteQuery(charQuery);
          setActiveIdx(0);
          return;
        }
      }

      setAutocompleteMode(null);
      setAutocompleteQuery("");
    },
    [onChange, campaignAssets.length, sessionCharacters.length]
  );

  const insertAssetRef = useCallback(
    (ca: CampaignAsset) => {
      const ta = textareaRef.current;
      if (!ta) return;
      const current = ta.value;
      const cursor = ta.selectionStart !== 0 ? ta.selectionStart : current.length;
      const before = current.slice(0, cursor);
      const after = current.slice(cursor);
      const triggerIdx = before.lastIndexOf("[[");
      if (triggerIdx === -1) return;
      const ref = `[[asset:${ca.id}|${ca.asset.name}]]`;
      onChange(current.slice(0, triggerIdx) + ref + after);
      setAutocompleteMode(null);
      requestAnimationFrame(() => {
        const newPos = triggerIdx + ref.length;
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      });
    },
    [onChange]
  );

  const insertCharRef = useCallback(
    (char: CharacterListItem) => {
      const ta = textareaRef.current;
      if (!ta) return;
      const current = ta.value;
      const cursor = ta.selectionStart !== 0 ? ta.selectionStart : current.length;
      const before = current.slice(0, cursor);
      const after = current.slice(cursor);
      const atIdx = before.lastIndexOf("@");
      if (atIdx === -1) return;
      const ref = `@[char:${char.id}|${char.name}]`;
      onChange(current.slice(0, atIdx) + ref + after);
      setAutocompleteMode(null);
      requestAnimationFrame(() => {
        const newPos = atIdx + ref.length;
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      });
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (autocompleteMode === null || dropdownItems === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, dropdownItems - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (autocompleteMode === "asset") insertAssetRef(filteredAssets[activeIdx]);
        else insertCharRef(filteredChars[activeIdx]);
      } else if (e.key === "Escape") {
        setAutocompleteMode(null);
      }
    },
    [
      autocompleteMode,
      dropdownItems,
      activeIdx,
      filteredAssets,
      filteredChars,
      insertAssetRef,
      insertCharRef,
    ]
  );

  // ── Markdown components ────────────────────────────────────────────────────

  const mdComponents = useMemo<Components>(
    () => ({
      span({ node, children, ...rest }) {
        // The rehypeAssetRef plugin stashes ref metadata in the hast node's
        // properties; read them from there (typed) rather than off DOM props.
        const props = node?.properties ?? {};
        const assetIdStr = props["dataAssetId"] ?? props["data-asset-id"];
        const assetDisplay = props["dataAssetDisplay"] ?? props["data-asset-display"];
        const charIdStr = props["dataCharId"] ?? props["data-char-id"];
        const charDisplay = props["dataCharDisplay"] ?? props["data-char-display"];
        if (assetIdStr) {
          return (
            <AssetRefChip
              assetId={Number(assetIdStr)}
              displayName={String(assetDisplay ?? children)}
              assetsMap={assetsMap}
              theme={theme}
            />
          );
        }
        if (charIdStr) {
          return (
            <CharacterMentionChip
              charId={Number(charIdStr)}
              displayName={String(charDisplay ?? children)}
              charactersMap={charactersMap}
              theme={theme}
            />
          );
        }
        return <span {...rest}>{children}</span>;
      },
      a({ href, children }) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: theme.colors.accent, textDecoration: "underline" }}
          >
            {children}
          </a>
        );
      },
      h1({ children }) {
        return (
          <h1
            className="text-base font-bold mt-2 mb-1 border-b pb-0.5"
            style={{ color: theme.colors.textPrimary, borderColor: theme.colors.sidebarBorder }}
          >
            {children}
          </h1>
        );
      },
      h2({ children }) {
        return (
          <h2 className="text-sm font-bold mt-2 mb-1" style={{ color: theme.colors.textPrimary }}>
            {children}
          </h2>
        );
      },
      h3({ children }) {
        return (
          <h3
            className="text-xs font-bold mt-1.5 mb-0.5"
            style={{ color: theme.colors.textSecondary }}
          >
            {children}
          </h3>
        );
      },
      p({ children }) {
        return (
          <p className="mb-1.5 leading-relaxed text-xs" style={{ color: theme.colors.textPrimary }}>
            {children}
          </p>
        );
      },
      ul({ children }) {
        return (
          <ul
            className="list-disc list-inside mb-1.5 space-y-0.5 text-xs"
            style={{ color: theme.colors.textPrimary }}
          >
            {children}
          </ul>
        );
      },
      ol({ children }) {
        return (
          <ol
            className="list-decimal list-inside mb-1.5 space-y-0.5 text-xs"
            style={{ color: theme.colors.textPrimary }}
          >
            {children}
          </ol>
        );
      },
      blockquote({ children }) {
        return (
          <blockquote
            className="border-l-2 pl-2 my-1 italic text-xs"
            style={{ borderColor: theme.colors.accent, color: theme.colors.textSecondary }}
          >
            {children}
          </blockquote>
        );
      },
      code({ children, className }) {
        const isBlock = !!className?.includes("language-");
        return isBlock ? (
          <code>{children}</code>
        ) : (
          <code
            className="px-1 rounded text-xs font-mono"
            style={{ backgroundColor: theme.colors.surface, color: theme.colors.accent }}
          >
            {children}
          </code>
        );
      },
      pre({ children }) {
        return (
          <pre
            className="rounded p-2 text-xs overflow-x-auto my-1 font-mono leading-relaxed"
            style={{ backgroundColor: theme.colors.surface, color: theme.colors.textPrimary }}
          >
            {children}
          </pre>
        );
      },
      hr() {
        return <hr className="my-2" style={{ borderColor: theme.colors.sidebarBorder }} />;
      },
      strong({ children }) {
        return (
          <strong className="font-bold" style={{ color: theme.colors.textPrimary }}>
            {children}
          </strong>
        );
      },
      em({ children }) {
        return (
          <em className="italic" style={{ color: theme.colors.textSecondary }}>
            {children}
          </em>
        );
      },
    }),
    [assetsMap, charactersMap, theme]
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  const showAssetDropdown = autocompleteMode === "asset" && filteredAssets.length > 0;
  const showCharDropdown = autocompleteMode === "character" && filteredChars.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      {!readOnly && (
        <div
          className="flex items-center gap-1 px-2 py-1 flex-shrink-0 border-b"
          style={{
            borderColor: theme.colors.sidebarBorder,
            backgroundColor: theme.colors.panelHeader,
          }}
        >
          <button
            type="button"
            onClick={() => setMode("edit")}
            className="px-2 py-0.5 rounded text-xs transition-colors"
            style={{
              backgroundColor: mode === "edit" ? theme.colors.accent : "transparent",
              color: mode === "edit" ? "#fff" : theme.colors.textMuted,
            }}
            data-testid={`${testId}-btn-edit`}
          >
            ✏️ Edit
          </button>
          <button
            type="button"
            onClick={() => setMode("preview")}
            className="px-2 py-0.5 rounded text-xs transition-colors"
            style={{
              backgroundColor: mode === "preview" ? theme.colors.accent : "transparent",
              color: mode === "preview" ? "#fff" : theme.colors.textMuted,
            }}
            data-testid={`${testId}-btn-preview`}
          >
            👁 Preview
          </button>
          <span className="ml-2 text-xs hidden sm:inline" style={{ color: theme.colors.textMuted }}>
            Markdown ·{" "}
            <code
              className="px-0.5 rounded"
              style={{ backgroundColor: theme.colors.surface, color: theme.colors.accent }}
            >
              [[
            </code>{" "}
            asset ·{" "}
            <code
              className="px-0.5 rounded"
              style={{ backgroundColor: theme.colors.surface, color: "#06b6d4" }}
            >
              @
            </code>{" "}
            character
          </span>
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {mode === "edit" ? (
          <>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              readOnly={readOnly}
              placeholder={placeholder}
              className="w-full h-full resize-none p-3 text-sm focus:outline-none font-mono leading-relaxed"
              style={{
                backgroundColor: theme.colors.panelBg,
                color: theme.colors.textPrimary,
                caretColor: theme.colors.accent,
              }}
              spellCheck={true}
              data-testid={testId}
            />

            {/* Asset autocomplete dropdown */}
            {showAssetDropdown && (
              <div
                className="absolute top-0 left-0 right-0 z-50 border-b overflow-auto max-h-40 codex-scroll"
                style={{
                  backgroundColor: theme.colors.panelHeader,
                  borderColor: theme.colors.panelBorder,
                }}
                data-testid="asset-autocomplete-dropdown"
              >
                {filteredAssets.map((ca, i) => (
                  <button
                    key={ca.id}
                    type="button"
                    onClick={() => insertAssetRef(ca)}
                    className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:opacity-90"
                    style={{
                      backgroundColor: i === activeIdx ? theme.colors.accent : "transparent",
                      color: i === activeIdx ? "#fff" : theme.colors.textPrimary,
                    }}
                    data-testid={`asset-autocomplete-item-${ca.id}`}
                  >
                    <span className="text-xs font-medium truncate">{ca.asset.name}</span>
                    <span
                      className="text-xs shrink-0"
                      style={{
                        color: i === activeIdx ? "rgba(255,255,255,0.7)" : theme.colors.textMuted,
                      }}
                    >
                      {ca.asset.type}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Character mention autocomplete dropdown */}
            {showCharDropdown && (
              <div
                className="absolute top-0 left-0 right-0 z-50 border-b overflow-auto max-h-40 codex-scroll"
                style={{
                  backgroundColor: theme.colors.panelHeader,
                  borderColor: "#06b6d455",
                }}
                data-testid="char-autocomplete-dropdown"
              >
                {filteredChars.map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => insertCharRef(c)}
                    className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:opacity-90"
                    style={{
                      backgroundColor: i === activeIdx ? "#06b6d4" : "transparent",
                      color: i === activeIdx ? "#fff" : theme.colors.textPrimary,
                    }}
                    data-testid={`char-autocomplete-item-${c.id}`}
                  >
                    <span className="text-xs opacity-60">@</span>
                    <span className="text-xs font-medium truncate">{c.name}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div
            className="h-full overflow-y-auto p-3 codex-scroll"
            style={{
              backgroundColor: theme.colors.panelBg,
              color: theme.colors.textPrimary,
            }}
            data-testid={`${testId}-preview`}
          >
            {value ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={REHYPE_PLUGINS}
                components={mdComponents}
              >
                {value}
              </ReactMarkdown>
            ) : (
              <p className="text-xs" style={{ color: theme.colors.textMuted }}>
                No notes yet.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
